
import { useState } from "react";
import { useFetchConversation } from "./conversation/use-fetch-conversation";
import { useGenerateResponse } from "./conversation/use-generate-response";
import { useGenerateChargeback } from "./conversation/use-generate-chargeback";
import { usePasteSellerResponse } from "./conversation/use-paste-seller-response";
import { MessageTone, DBMessage, mapDBMessageToUIMessage, Message } from "@/types/message";
import { useToast } from "./use-toast";
import { useRegenerateResponse } from "./conversation/use-regenerate-response";
import { useMessageOperations } from "./conversation/use-message-operations";

export const useConversation = (id: string | undefined) => {
  const { toast } = useToast();
  
  // Core data fetching and state management
  const { 
    conversation, 
    messages, 
    loading, 
    refreshing, 
    setMessages, 
    refreshMessages,
    updateConversationFormat
  } = useFetchConversation(id);
  
  // API interaction hooks
  const { 
    handleGenerateResponse, 
    handleRegenerateAIResponse,
    isGenerating, 
    isRegenerating,
    regeneratingMessageId
  } = useGenerateResponse(id);
  
  const { 
    handleGenerateReport, 
    isGeneratingChargeback, 
    isDialogOpen, 
    handleDialogOpenChange, 
    handleOpenDisputeDialog 
  } = useGenerateChargeback();
  
  const { 
    handlePasteSellerResponse, 
    isPasting, 
    isProcessingAI 
  } = usePasteSellerResponse(
    id,
    async (conv, msgs, content) => {
      // Generate the AI response and return it
      return await handleGenerateResponse(conv, msgs, content);
    }
  );

  // Operation hooks
  const { regenerateAIResponse } = useRegenerateResponse(id, refreshMessages);
  const { generateResponse, pasteSellerResponse } = useMessageOperations(id);

  // Wrapped operations that connect the API handlers with state management
  const generateResponseWrapper = async (sellerResponseContent?: string): Promise<Message | null> => {
    console.log("🚀 useConversation: generateResponseWrapper called with format:", conversation?.message_format);
    
    const dbMessage = await generateResponse(
      conversation, 
      messages, 
      sellerResponseContent, 
      setMessages, 
      handleGenerateResponse
    );
    
    // CRITICAL: Don't refresh messages as it could overwrite the format
    // The message list is already updated via setMessages in the generateResponse function
    console.log("✅ useConversation: Generated response completed, preserving format:", conversation?.message_format);
    
    return dbMessage ? mapDBMessageToUIMessage(dbMessage) : null;
  };

  // Updated to include customInstructions parameter
  const regenerateAIResponseWrapper = async (messageId: string, tone: MessageTone, customInstructions?: string): Promise<Message | null> => {
    console.log("🔄 useConversation: regenerateAIResponseWrapper called with format:", conversation?.message_format);
    
    const dbMessage = await regenerateAIResponse(
      conversation,
      messages,
      messageId,
      tone,
      setMessages,
      handleRegenerateAIResponse,
      customInstructions
    );
    return dbMessage ? mapDBMessageToUIMessage(dbMessage) : null;
  };

  const pasteSellerResponseWrapper = async (content: string): Promise<void> => {
    console.log("📝 useConversation: pasteSellerResponseWrapper called with format:", conversation?.message_format);
    
    await pasteSellerResponse(
      conversation, 
      messages, 
      content, 
      setMessages, 
      handlePasteSellerResponse
    );
  };

  return {
    // Data
    conversation,
    messages,
    loading,
    refreshing,
    
    // Status flags
    isGenerating,
    isRegenerating,
    regeneratingMessageId,
    isGeneratingChargeback,
    isPasting,
    isProcessingAI,
    isDialogOpen,
    
    // UI handlers
    handleDialogOpenChange,
    handleOpenDisputeDialog,
    updateConversationFormat,
    
    // Main operations
    handleGenerateResponse: generateResponseWrapper,
    handleRegenerateAIResponse: regenerateAIResponseWrapper,
    handleGenerateReport,
    handlePasteSellerResponse: pasteSellerResponseWrapper,
    refreshMessages
  };
};
